// useSymbolDragDrop.js — drag-and-drop symbol placement onto the map canvas (ticket 09
// extraction). Independent of useMapViews; both were pulled out of MapEditorModalInner in the
// same ticket since they're small, self-contained concerns.

import { useCallback, useRef } from 'react';
import { toLonLat } from 'ol/proj';

export default function useSymbolDragDrop({ mapRef, interactionRef }) {
    // Ticket 03 (map-editor-audit): nothing in MapEditorModal.jsx's JSX ever reads a drag-over
    // highlight, so tracking this via `useState` re-rendered the entire modal tree on every
    // dragenter/dragleave for zero visual payoff. A ref preserves the same value across renders
    // (still useful if a future drag-over highlight is wired up) without triggering any of them.
    const isDragOverRef = useRef(false);

    const handleMapDrop = useCallback((e) => {
        e.preventDefault();
        isDragOverRef.current = false;
        const sidc = e.dataTransfer.getData('text/x-sidc');
        if (!sidc) return;
        const map = mapRef.current;
        if (!map) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pixel = [e.clientX - rect.left, e.clientY - rect.top];
        const coord = map.getCoordinateFromPixel(pixel);
        if (!coord) return;
        const [lng, lat] = toLonLat(coord);
        interactionRef.current?._emit('finish', { geomType: 'Point', geometry: { type: 'Point', coordinates: [lng, lat] }, sidc });
    }, [mapRef, interactionRef]);

    // Per-spec, `dataTransfer.getData()` is only reliably readable on `drop` in most browsers
    // (returns '' during dragenter/dragover in Chrome/Safari) — but `dataTransfer.types` (just
    // the list of available MIME types, not the payload) *is* readable during dragenter/
    // dragover, so it's used here to gate the highlight to actual symbol drags from the sidebar
    // palette. Falls back to showing the highlight if `types` isn't available for some reason
    // (permissive — matches the ticket's "or shows it without breaking anything" fallback).
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        const types = e.dataTransfer?.types;
        if (!types || Array.prototype.includes.call(types, 'text/x-sidc')) {
            isDragOverRef.current = true;
        }
    }, []);

    const handleDragLeave = useCallback((e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        isDragOverRef.current = false;
    }, []);

    const handleDragOver = useCallback((e) => e.preventDefault(), []);

    return { handleMapDrop, handleDragEnter, handleDragLeave, handleDragOver };
}
